// Generators: gen meal/work/str/spesa entry points + gates pre-genAll +
// meal composer JS-driven (PR-4 ADR-0006).
//
// Dipendenze cross-modulo (ESM):
//   core.js     → api, cj, fixJson, HAIKU
//   prompts.js  → workP1, workP2, strP
//   builders.js → savePlan, persistMealPlanStructured, logGenError
//
// Dipendenze runtime da <script> classic inline (resolved via window.* a
// runtime, accessibili in ESM strict mode come bare identifier per READ):
//   window.S, window.PS              → state in-memory
//   window.sb                        → Supabase client (single instance)
//   window.saveProfile               → builders bridge (per gates onboarding_flags)
//   setTile, updateDL, addMsg, addSys → UI helpers (function decls top-level)
//   openChat, closeChat               → chat panel toggle
//
// PR-4 (ADR-0006) refactor genMeal: composer JS-driven sostituisce
// Haiku-driven (vedi `composeMealSkeleton` + `ciqualEnrich` + `haikuFillDetails`).
// Constraint adherence 100% by construction: il composer sceglie SOLO da
// public.foods filtrato per styles + intolleranze, Haiku scrive solo
// nm/sb/st (descrizioni piatti).

import { api, cj, fixJson, HAIKU } from './core.js';
import { workP1, workP2, strP, workEnrich, strEnrich } from './prompts.js';
import { savePlan, persistMealPlanStructured, logGenError } from './builders.js';
import { composeFromRecipe, preloadCompatibleRecipes, clearRecipeCache, ConstraintTracker, fetchPersistentHistory, persistPlanToHistory } from './meal-recipe-composer.js';
import { SLOT_RATIOS } from './method-facts.js';

// ─── MEAL COMPOSER — ADR-0006 (PR-4) ──────────────────────────────────────
//
// Architettura a 3 step deterministico → LLM-writer:
// 1. composeMealSkeleton(profile, sb): pool filter SQL + scelta food + qty
// 2. ciqualEnrich(skeleton): sanity check macro + day totals
// 3. haikuFillDetails(skeleton): Haiku scrive nm/sb/st (1 call totale)
//
// genMeal() orchestra i 3 step + persiste su plans/meal_plan_items.

// Distribuzione kcal per slot (somma = 100%). Standard nutrizione.
// SLOT_RATIOS vive in method-facts.js (fonte unica: la stessa ripartizione
// è mostrata all'utente sulla pagina /metodo).

// ─── Phase 1 ADR-0013 (Strategy E): legacy affinity machinery rimossa ─────
// Tutto il scoring tier × jaccard × variety_penalty (Track A + Track B) è
// stato sostituito dal composer template-first in `meal-recipe-composer.js`,
// che pesca ricette autentiche da public.meal_recipes (2083 ricette misya IT)
// e le scala per kcal target. food_affinities resta in DB solo per
// tracciabilità storica (cultural_origin='mixed_track_b'), composer non
// la consulta più.

// Mappa intolleranza testuale → predicati sul pool foods.
// Allineata a public.foods.protein_source/carb_source/fat_source + canonical_name.
// Vedi scripts/brief_preferenze_alimentari.md per vocabolario.
const INTOLERANCE_MAP = {
  carne:              { exclude_protein_source: ['carne_rossa','carne_bianca'] },
  pesce:              { exclude_protein_source: ['pesce','crostacei_molluschi'] },
  crostacei:          { exclude_protein_source: ['crostacei_molluschi'] },
  molluschi:          { exclude_protein_source: ['crostacei_molluschi'] },
  lattosio:           { exclude_protein_source: ['latticini'] },
  latticini:          { exclude_protein_source: ['latticini'] },
  latte:              { exclude_protein_source: ['latticini'] },
  uova:               { exclude_protein_source: ['uova'] },
  glutine:            { exclude_keywords: ['grano','frumento','pasta','pane','farro','orzo','segale','seitan','couscous','cracker','biscotti','muesli','granola','focaccia','pizza'] },
  'frutta secca':     { exclude_protein_source: ['frutta_secca_proteica'], exclude_fat_source: ['frutta_secca_grassa'] },
  'frutta a guscio':  { exclude_protein_source: ['frutta_secca_proteica'], exclude_fat_source: ['frutta_secca_grassa'] },
  arachidi:           { exclude_keywords: ['arachidi','arachide'] },
  soia:               { exclude_protein_source: ['soia_derivati'] },
  sedano:             { exclude_keywords: ['sedano'] },
  senape:             { exclude_keywords: ['senape','mostarda'] },
  sesamo:             { exclude_keywords: ['sesamo','tahina','tahin'] },
  miele:              { exclude_keywords: ['miele'] },
  solfiti:            { exclude_keywords: ['vino','aceto balsamico'] },
  lupini:             { exclude_keywords: ['lupini'] }
};

// Mappa intolleranza → styles canonici Coach AI (subset di onnivoro/pescetariano/vegetariano/vegano)
// che l'utente PUO mangiare. Allineata a public.foods.styles[].
//
// IMPORTANTE: la nota "alterna ricette vegetariane e vegane" NON significa
// che l'utente sia vegan/vegetarian. Significa che vuole VARIETÀ con piatti
// veg/vegan in aggiunta al suo stile base. Lo stile base resta determinato
// SOLO da intolleranze[] (campo atomico) + menzioni esplicite stile principale
// nella nota ("sono vegano", "vegano stretto"). Le menzioni "veg/vegan" in
// contesto di alternanza/varietà espandono il pool ma non cambiano lo stile.
function deriveStyles(profile) {
  const intol = (profile.intolleranze || []).map(x => String(x).toLowerCase());
  const note = (profile.note_alimentari || '').toLowerCase();

  const intolExcludesMeat = intol.some(x => /\b(carne|manzo|pollo|tacchino|maiale|agnello)\b/.test(x));
  const intolExcludesFish = intol.some(x => /pesc|salmone|tonno|crostace/.test(x));
  const intolExcludesDairy = intol.some(x => /lattosio|latticin|latte|formagg/.test(x));
  const intolExcludesEggs = intol.some(x => /\buova?\b/.test(x));

  // "Alternato" = vuole varietà con piatti veg/vegan ma stile base resta carnivore/pescatarian
  const noteAlternato = /\b(alterna|alternat|varieta|variet[àa]|mix|misto)\b/.test(note);
  // Menzioni esplicite stile principale (PERSONA è vegan/vegetariana, no alternanza)
  const noteIsStrictlyVegan = /\b(sono\s+vegan|vegano\s+stretto|vegano\s+puro|100\s*%\s*vegetale|solo\s+vegetal)/.test(note) ||
                              (intolExcludesMeat && intolExcludesFish && intolExcludesDairy && intolExcludesEggs);
  const noteIsStrictlyVeget = (!noteAlternato && /\b(sono\s+vegetarian|vegetariana?\b)/.test(note) && !noteIsStrictlyVegan) ||
                              (intolExcludesMeat && intolExcludesFish && !intolExcludesDairy && !intolExcludesEggs);

  // BASE STYLE: determinato da intolleranze atomiche + menzioni stile esplicite (no alternanza)
  let baseStyle;
  if (noteIsStrictlyVegan) baseStyle = 'vegano';
  else if (noteIsStrictlyVeget) baseStyle = 'vegetariano';
  else if (intolExcludesMeat && !intolExcludesFish) baseStyle = 'pescetariano';
  else baseStyle = 'onnivoro';  // include "carne_sì_pesce_no" (è onnivoro con override pesce a runtime)

  // EXPANDED STYLES: se nota dice "alterna" + veg/vegan, espandi pool per varietà
  // (es. onnivoro alternato vede anche piatti vegetariani/vegani nel pool)
  const styles = [baseStyle];
  if (noteAlternato) {
    if (/\bvegetarian|veget|veg\b/.test(note) && baseStyle !== 'vegetariano') styles.push('vegetariano');
    if (/\bvegan/.test(note) && baseStyle !== 'vegano') styles.push('vegano');
  }

  // isStrictVegan flag attivo solo per "vegano stretto" testuale
  const isStrictVegan = /vegano\s+stretto|vegano\s+puro|100\s*%\s*vegetale/.test(note);

  return { styles: [...new Set(styles)], isStrictVegan };
}

// Carica pool foods da public.foods filtrato per styles + intolleranze.
// Throw se pool insufficient (< 20 totali o < 4 proteine).
async function loadFoodsPool(sb, profile) {
  const { styles, isStrictVegan } = deriveStyles(profile);

  // BKL-048 fix: PostgREST hard limit 1000 rows. Pagination loop + projection.
  const PAGE_SIZE = 1000;
  const all = [];
  let _from = 0;
  while (true) {
    const { data, error } = await sb.from('foods')
      .select('canonical_name,display_name,category,styles,cultural_tier,kcal_per_100g,proteine_g_per_100g,carbo_g_per_100g,grassi_g_per_100g,protein_source,carb_source,fat_source,is_main_meal_eligible')
      .range(_from, _from + PAGE_SIZE - 1);
    if (error) throw new Error('loadFoodsPool: ' + error.message);
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
    _from += PAGE_SIZE;
    if (_from > 50000) break;
  }
  if (!all.length) throw new Error('public.foods è vuoto');

  // Filter JS (più semplice di query complex per overlaps + multi exclude)
  let pool = all.filter(f => {
    // 1. Match almeno uno style utente
    const fStyles = f.styles || [];
    if (!styles.some(s => fStyles.includes(s))) return false;
    // 2. Esclude foods senza macro reali (curated NULL — Task 6 fix curation post-Task 8)
    if (f.kcal_per_100g == null || f.proteine_g_per_100g == null || f.carbo_g_per_100g == null || f.grassi_g_per_100g == null) return false;
    // 3. Esclude tier-E (PR #62 pool cultural audit: kombu/wasabi/miso/harissa/
    //    dukkah/papaya/manioca/plantano/goji/alghe esotiche/cavolo cinese/
    //    uova di quaglia). Sono nel DB per completezza CIQUAL ma fuori dalla
    //    dieta mediterranea allargata. Safety net JS, idempotente quando lo
    //    script SQL drop sarà eseguito (scripts/foods-audit-merge-drop.sql).
    if (f.cultural_tier === 'E') return false;
    return true;
  });

  // Apply intolleranze
  const intol = (profile.intolleranze || []).map(x => String(x).toLowerCase());
  for (const i of intol) {
    const rule = INTOLERANCE_MAP[i];
    if (!rule) continue;
    if (rule.exclude_protein_source) {
      pool = pool.filter(f => !rule.exclude_protein_source.includes(f.protein_source));
    }
    if (rule.exclude_carb_source) {
      pool = pool.filter(f => !rule.exclude_carb_source.includes(f.carb_source));
    }
    if (rule.exclude_fat_source) {
      pool = pool.filter(f => !rule.exclude_fat_source.includes(f.fat_source));
    }
    if (rule.exclude_keywords) {
      pool = pool.filter(f => !rule.exclude_keywords.some(kw =>
        f.canonical_name.toLowerCase().includes(kw)));
    }
  }

  // Vegano stretto: rimuove miele + gelatina + additivi animali (best-effort keyword)
  if (isStrictVegan) {
    pool = pool.filter(f => !/miele|gelatina/i.test(f.canonical_name));
  }

  // Iter 3: blacklist alimenti esotici non-tradizionali italiani.
  // Sono nel DB (CIQUAL + curated) per completezza ma Sonnet judge li vede
  // come non-italiani → L2 realismo basso. Rimuoverli dal pool migliora
  // significativamente il "feel italiano" dei piani generati.
  const EXOTIC_BLACKLIST = /kombu|dukkah|freekeh|wasabi|miso|kaki|guava|maracuja|carambola|durian|jackfruit|sambuco|lichi|litchi/i;
  pool = pool.filter(f => !EXOTIC_BLACKLIST.test(f.canonical_name));

  // Esclusioni puntuali da note_alimentari ("niente pollo", "no funghi", "non mi piace ...")
  // Estrae parole specifiche di alimenti che l'utente esclude per gusto/preferenza
  // (separate dalle intolleranze atomiche). Pattern flessibile: cerca le parole
  // dopo "no"/"niente"/"odio"/"non mi piace"/"non mangio" e filtra il pool.
  const note = (profile.note_alimentari || '').toLowerCase();
  const exclusionPatterns = note.matchAll(/(?:^|\b)(?:no|niente|odio|non\s+mi\s+piace|non\s+mangio)\s+([a-zà-ù]+(?:\s+[a-zà-ù]+){0,2})/g);
  const noteExclusions = [];
  for (const m of exclusionPatterns) {
    // Skip se la nota è "no carne/pesce/latticini/uova" — già gestita da INTOLERANCE_MAP
    const phrase = m[1].trim();
    const firstWord = phrase.split(/\s+/)[0];
    if (firstWord.length < 4) continue;
    if (/^(carne|pesce|latticini?|latte|uova|formagg|crostac|moll|gluten|lattos)/.test(firstWord)) continue;
    if (/^(altre|altri|cibi|cose|preferenze|particolari)/.test(firstWord)) continue;
    noteExclusions.push(firstWord);
  }
  if (noteExclusions.length) {
    pool = pool.filter(f => !noteExclusions.some(kw =>
      f.canonical_name.toLowerCase().includes(kw)));
  }

  // Validazione pool sufficient
  if (pool.length < 20) {
    throw new Error('POOL_INSUFFICIENT: solo ' + pool.length + ' alimenti compatibili con il profilo. Le intolleranze sono troppe — chiedi all\'utente di rilassarne almeno una.');
  }
  const proteins = pool.filter(f => f.category === 'proteine' && f.is_main_meal_eligible);
  if (proteins.length < 4) {
    throw new Error('POOL_PROTEIN_INSUFFICIENT: solo ' + proteins.length + ' proteine main-meal compatibili. Profilo troppo restrittivo.');
  }
  const carbs = pool.filter(f => f.category === 'carboidrati');
  if (carbs.length < 3) {
    throw new Error('POOL_CARB_INSUFFICIENT: solo ' + carbs.length + ' carbo compatibili.');
  }
  const verdure = pool.filter(f => f.category === 'verdure');
  if (verdure.length < 4) {
    throw new Error('POOL_VERDURA_INSUFFICIENT: solo ' + verdure.length + ' verdure compatibili.');
  }

  return { pool, styles, isStrictVegan };
}

// ─── Phase 1 ADR-0013: composer legacy rimosso ────────────────────────────
// MAIN_PROTEIN_BY_STYLE + composeMainMeal/Breakfast/Snack erano il composer
// pool-based ADR-0006/Track B. Sostituiti da `meal-recipe-composer.js`
// (template-first su public.meal_recipes). Le 4 funzioni rimosse erano
// ~225 LOC che cercavano coerenza culturale via affinity scoring; ora la
// coerenza è data direttamente dalle ricette autentiche misya.

// STEP 1: Compone scheletro 7 giorni pescando ricette da public.meal_recipes.
// Phase 1 ADR-0013: composer template-first. Per ogni slot del giorno chiama
// `composeFromRecipe` (in meal-recipe-composer.js) che pesca una ricetta
// compatibile col profilo (style/intolleranze filters via excluded_*) e la
// scala per kcal target. Pranzo/cena attingono al pool ~1100 ricette ciascuno,
// spuntini a 339 ricette, colazione a 10 breakfast patterns IT hardcoded +
// 8 ricette DB di fallback.
//
// Compatibilità: item shape identico al composer precedente (pp/cc/gg/ig/mc/
// nm/sb/st), così UI/builders/persistenza non richiedono modifiche.
export async function composeMealSkeleton(profile, sb) {
  const supabase = sb || (typeof window !== 'undefined' ? window.sb : null);
  if (!supabase) throw new Error('composeMealSkeleton: Supabase client mancante (parametro sb o window.sb)');

  // Pool foods serve solo per foodCache (recompute macro post-scaling in composer
  // + rebalanceDay successivo). Composer NON pesca più da pool — pesca da meal_recipes.
  const pool = await loadFoodsPool(supabase, profile);
  const foodCache = new Map();
  for (const f of pool.pool) foodCache.set(f.canonical_name.toLowerCase(), f);

  // Preload + cache ricette compatibili (1 query SQL, ~2083 → filtered)
  clearRecipeCache();
  await preloadCompatibleRecipes(supabase, profile);

  const NOMI = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
  const GIORNI = ['Lunedì','Martedì','Mercoledì','Giovedì','Venerdì','Sabato','Domenica'];
  const TAGS = mealDayTags(profile.giorni_allenamento || 3);
  const slotEmoji = { col: '🌅', sna1: '🍎', pra: '🍝', sna2: '🍌', cen: '🌙' };
  const slotTitle = { col: 'Colazione', sna1: 'Spuntino 1', pra: 'Pranzo', sna2: 'Spuntino 2', cen: 'Cena' };

  // Target kcal per slot (somma = totalKcal). Riusa SLOT_RATIOS.
  const totalKcal = profile.kcal_target || 2000;
  const slotKcal = {
    col:  totalKcal * SLOT_RATIOS.col,
    sna1: totalKcal * SLOT_RATIOS.sna1,
    pra:  totalKcal * SLOT_RATIOS.pra,
    sna2: totalKcal * SLOT_RATIOS.sna2,
    cen:  totalKcal * SLOT_RATIOS.cen
  };

  const days = [];
  const history = [];
  // Phase 1.11: ConstraintTracker per regole frequenza (max_per_giorno/settimana)
  const tracker = new ConstraintTracker(profile.preferenze);
  // Phase 5b F9: fetch persistent history cross-generation (ultime 4 settimane)
  // per applicare penalty su ricette già viste dall'utente. Non blocking: in
  // caso di errore composer procede senza penalty.
  const profileId = profile && (profile.id || profile.user_id);
  if (profileId) {
    const persistentHistory = await fetchPersistentHistory(supabase, profileId, 28);
    tracker.setPersistentHistory(persistentHistory);
  }

  for (let i = 0; i < 7; i++) {
    // Phase 5b: same-day cross-check varietà. Passo gli items già scelti
    // oggi come `sameDayItems` per evitare es. pera+parmigiano in sna1 E sna2
    // dello stesso giorno (problema osservato pre-fix nello screen utente).
    const breakfast = await composeFromRecipe(supabase, 'col',  profile, history, slotKcal.col,  foodCache, tracker, i, []);
    const snack1    = await composeFromRecipe(supabase, 'sna1', profile, history, slotKcal.sna1, foodCache, tracker, i, [breakfast]);
    const lunch     = await composeFromRecipe(supabase, 'pra',  profile, history, slotKcal.pra,  foodCache, tracker, i, [breakfast, snack1]);
    const snack2    = await composeFromRecipe(supabase, 'sna2', profile, history, slotKcal.sna2, foodCache, tracker, i, [breakfast, snack1, lunch]);
    const dinner    = await composeFromRecipe(supabase, 'cen',  profile, history, slotKcal.cen,  foodCache, tracker, i, [breakfast, snack1, lunch, snack2]);

    // History anti-rotation: variety_key + anchor protein/carbo per ogni slot.
    // Phase 5b (Meal Data Quality Overhaul): aggiunto anchor name per col/sna1/
    // sna2 oltre a pra/cen (prima solo loro). Risolve "ricotta col 2 giorni di
    // fila" — pattern con variety_key diversi ma stesso anchor protein
    // 'Ricotta di vacca' venivano pickati senza penalty cross-day.
    history.push({
      col_variety:  breakfast._variety_key,
      sna1_variety: snack1._variety_key,
      pra_variety:  lunch._variety_key,
      sna2_variety: snack2._variety_key,
      cen_variety:  dinner._variety_key,
      col_protein:  breakfast._protein_name,
      sna1_protein: snack1._protein_name,
      pra_protein:  lunch._protein_name,
      sna2_protein: snack2._protein_name,
      cen_protein:  dinner._protein_name,
      col_carbo:    breakfast._carbo_name,
      sna1_carbo:   snack1._carbo_name,
      pra_carbo:    lunch._carbo_name,
      sna2_carbo:   snack2._carbo_name,
      cen_carbo:    dinner._carbo_name
    });

    // Phase 1.5: NON eliminare _recipe_name qui — serve a haikuFillDetails
    // come hint per il rebranding (preserva carattere ricetta originale).
    // Cleanup avviene dentro haikuFillDetails dopo merge nm/sb/st.
    // Phase 5b F9: cleanup _variety_key/_protein_name/_carbo_name spostato
    // FUORI dal loop — persistPlanToHistory ha bisogno di leggerli post-loop.

    days.push({
      n: NOMI[i], f: GIORNI[i], tag: TAGS[i],
      m: [
        { t: 'col',  i: slotEmoji.col,  ti: slotTitle.col,  k: breakfast.mc.k, items: [breakfast] },
        { t: 'sna',  i: slotEmoji.sna1, ti: slotTitle.sna1, k: snack1.mc.k,    items: [snack1] },
        { t: 'pra',  i: slotEmoji.pra,  ti: slotTitle.pra,  k: lunch.mc.k,     items: [lunch] },
        { t: 'sna',  i: slotEmoji.sna2, ti: slotTitle.sna2, k: snack2.mc.k,    items: [snack2] },
        { t: 'cen',  i: slotEmoji.cen,  ti: slotTitle.cen,  k: dinner.mc.k,    items: [dinner] }
      ]
    });
  }

  // whyWeek: consuntivo dell'aderenza alle regole di frequenza sulla settimana
  // appena composta. Persiste nel JSON del piano (niente underscore) e alimenta
  // il pannello "Perche' questo piatto".
  const skeleton = { days, profile_snapshot: profile, whyWeek: tracker.weeklyRuleSummary(), _foodCache: foodCache };

  // Phase 5b F9: persist plan in meal_recipe_history per cross-generation
  // variety penalty in future calls. Non blocking — composer ritorna anche se
  // persist fallisce (graceful degrade).
  if (profileId) {
    const generationId = `gen-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    persistPlanToHistory(supabase, profileId, skeleton, generationId).catch(e => {
      console.warn('persistPlanToHistory failed (non-blocking):', e && e.message);
    });
  }

  // Phase 5b F9: cleanup meta fields (post-persist). _recipe_name resta —
  // serve a haikuFillDetails come hint per rebranding.
  for (const day of skeleton.days) {
    for (const meal of day.m) {
      for (const item of meal.items) {
        // _recipe_id kept intentionally: used by swapSingleRecipe (BKL-030)
        delete item._variety_key;
        delete item._protein_name;
        delete item._carbo_name;
        delete item._is_pattern;
        delete item._cultural_origin;
      }
    }
  }

  return skeleton;
}

// Multi-pass rebalance: 3 pass iterativi per chiudere il gap su P/C/G/kcal.
// Approccio: scala qty del food "dominant" per ogni macro (protein food per P,
// carbo food per C, olio per G), recompute item.mc, ripeti fino a convergenza
// o max 3 pass. Bound qty su range realistici (es. protein 40-250g, olio 2-40g).
function rebalanceDay(day, profile, foodCache) {
  if (!foodCache || foodCache.size === 0) return;

  const recomputeDayTotals = () => {
    let k = 0, p = 0, c = 0, g = 0;
    for (const meal of day.m) {
      for (const item of meal.items) {
        k += item.mc.k || 0;
        p += parseFloat(item.mc.p) || 0;
        c += parseFloat(item.mc.c) || 0;
        g += parseFloat(item.mc.f) || 0;
      }
    }
    return { k, p, c, g };
  };

  // Identifica ingredienti dominanti per ogni macro nei pasti principali
  const proteinIgs = []; // {item, idx, food}
  const carboIgs = [];
  const fatIgs = [];

  for (const meal of day.m) {
    for (const item of meal.items) {
      (item.ig || []).forEach((ig, idx) => {
        const food = foodCache.get(ig.n.toLowerCase());
        if (!food) return;
        if (food.category === 'proteine' && (meal.t === 'pra' || meal.t === 'cen' || meal.t === 'col')) {
          proteinIgs.push({ item, meal, idx, food });
        } else if (food.category === 'carboidrati') {
          carboIgs.push({ item, meal, idx, food });
        } else if (food.category === 'grassi') {
          fatIgs.push({ item, meal, idx, food });
        }
      });
    }
  }

  const target = {
    k: profile.kcal_target || 2000,
    p: profile.proteine_target || 130,
    c: profile.carbo_target || 250,
    g: profile.grassi_target || 70
  };

  const fixItemMc = (item) => {
    // Inline recompute (recomputeItemMc usa 'meal' undefined in closure)
    const totals = (item.ig || []).reduce((acc, ig) => {
      const food = foodCache.get(ig.n.toLowerCase());
      if (!food) return acc;
      const f = ig.q / 100;
      acc.k += (food.kcal_per_100g || 0) * f;
      acc.p += (food.proteine_g_per_100g || 0) * f;
      acc.c += (food.carbo_g_per_100g || 0) * f;
      acc.g += (food.grassi_g_per_100g || 0) * f;
      return acc;
    }, { k: 0, p: 0, c: 0, g: 0 });
    item.mc.k = Math.round(totals.k);
    item.mc.p = totals.p.toFixed(1) + 'g';
    item.mc.c = totals.c.toFixed(1) + 'g';
    item.mc.f = totals.g.toFixed(1) + 'g';
  };

  const scaleIgsForMacro = (igs, macroKey, currentTotal, fixedContribution, minQty, maxQty) => {
    // currentTotal = contributo attuale al macro dai food in igs
    // fixedContribution = contributo dai food NON in igs (non scalabile)
    // target_for_igs = target.macro - fixedContribution
    if (currentTotal <= 0) return;
    const otherContrib = fixedContribution;
    const targetForIgs = Math.max(currentTotal * 0.4, target[macroKey] - otherContrib);
    const ratio = targetForIgs / currentTotal;
    const clampedRatio = Math.max(0.4, Math.min(2.5, ratio));

    const itemsModified = new Set();
    for (const { item, idx } of igs) {
      const oldQ = item.ig[idx].q;
      const newQ = Math.max(minQty, Math.min(maxQty, Math.round((oldQ * clampedRatio) / 5) * 5));
      if (newQ !== oldQ) {
        item.ig[idx].q = newQ;
        itemsModified.add(item);
      }
    }
    for (const item of itemsModified) fixItemMc(item);
  };

  const computeMacroFromIgs = (igs, macroPer100g) => {
    return igs.reduce((s, { item, idx, food }) =>
      s + (food[macroPer100g] || 0) * item.ig[idx].q / 100, 0);
  };

  for (let pass = 0; pass < 3; pass++) {
    const totals = recomputeDayTotals();
    const errors = {
      k: Math.abs(totals.k - target.k) / target.k,
      p: Math.abs(totals.p - target.p) / target.p,
      c: Math.abs(totals.c - target.c) / target.c,
      g: Math.abs(totals.g - target.g) / target.g
    };
    // Tolerance interna 5% — più stretta di quella eval per dare margine
    if (errors.k < 0.05 && errors.p < 0.08 && errors.c < 0.08 && errors.g < 0.10) break;

    // PROTEIN: scala protein igs per matchare target.p
    if (errors.p > 0.05 && proteinIgs.length > 0) {
      const currentP = computeMacroFromIgs(proteinIgs, 'proteine_g_per_100g');
      const fixedP = totals.p - currentP;
      scaleIgsForMacro(proteinIgs, 'p', currentP, fixedP, 40, 250);
    }

    // CARBO: scala carbo igs
    if (errors.c > 0.05 && carboIgs.length > 0) {
      const totals2 = recomputeDayTotals();
      const currentC = computeMacroFromIgs(carboIgs, 'carbo_g_per_100g');
      const fixedC = totals2.c - currentC;
      scaleIgsForMacro(carboIgs, 'c', currentC, fixedC, 30, 200);
    }

    // GRASSO: scala fat igs (olio + nuts toppings)
    if (errors.g > 0.05 && fatIgs.length > 0) {
      const totals2 = recomputeDayTotals();
      const currentG = computeMacroFromIgs(fatIgs, 'grassi_g_per_100g');
      const fixedG = totals2.g - currentG;
      scaleIgsForMacro(fatIgs, 'g', currentG, fixedG, 3, 50);
    }
  }
}

// STEP 2: Validation + day totals aggregate + multi-pass rebalance qty per
// matchare i 4 macro target (P/C/G/kcal). composeMealSkeleton popola macro
// reali dai foods, ciqualEnrich aggrega + rebalance iterativo.
export async function ciqualEnrich(skeleton) {
  const profile = skeleton.profile_snapshot || {};
  const foodCache = skeleton._foodCache; // populated in composeMealSkeleton

  for (const day of skeleton.days) {
    // Multi-pass rebalance: scala qty protein/carbo/fat igs per chiudere gap target
    if (profile.kcal_target && foodCache) rebalanceDay(day, profile, foodCache);

    let totalK = 0, totalP = 0, totalC = 0, totalF = 0;
    for (const meal of day.m) {
      for (const item of meal.items) {
        totalK += item.mc.k || 0;
        totalP += parseFloat(item.mc.p) || 0;
        totalC += parseFloat(item.mc.c) || 0;
        totalF += parseFloat(item.mc.f) || 0;
      }
      meal.k = meal.items.reduce((s, it) => s + (it.mc.k || 0), 0);
    }
    day.totals = {
      kcal: Math.round(totalK),
      p_g: +totalP.toFixed(1),
      c_g: +totalC.toFixed(1),
      f_g: +totalF.toFixed(1)
    };
  }
  // Cleanup internal cache prima di output (non serve a downstream)
  delete skeleton._foodCache;
  return skeleton;
}

// STEP 3: Haiku writer riempie nm/sb/st per ogni item.
// Output Haiku in formato compatto: array piatto di {nm,sb,st} ordinato per
// row-major su skeleton (day, meal, item). Riduce verbosità output ~70% vs
// schema completo, sta comodamente sotto max_tokens=4000 in 1 sola call.
export async function haikuFillDetails(skeleton) {
  // Phase 1.5 ADR-0013: Haiku riceve il NOME ORIGINALE della ricetta come
  // hint. Se il nome è già pulito (es. "Frittata di carciofi") deve solo
  // raffinarlo; se è nome-elenco da scraper (es. "Pasta ceci patate cipolla")
  // deve trasformarlo in nome elegante ("Pasta e ceci con patate").
  // Regole rigide su aggettivi (NO "ricotta integrale" se l'integrale è il
  // pane), no carni processate nel nome principale, snack=snack.
  const SYS = `Sei un food writer italiano per un'app di nutrizione. Ricevi pasti con NOME ORIGINALE della ricetta + ingredienti fissi + slot + tag TEMPO. Per ogni pasto produci:
- nm: nome elegante max 6 parole, italiano
- sb: sottotitolo 5-10 parole (sapore/tecnica/carattere)
- st: 1-3 step istruzioni concrete

REGOLE CRITICHE:
1. Se NOME ORIGINALE è già pulito (es. "Frittata di carciofi", "Pane integrale con ricotta e miele"), USALO COSI COM'E o raffina solo leggermente la forma. Se è elenco di ingredienti grezzo (es. "Pasta ceci patate cipolla"), TRASFORMA in nome ricetta vero (es. "Pasta e ceci con patate alla contadina").
2. NON INVERTIRE l'ordine semantico: se ORIG dice "Pane con ricotta", il nuovo nome resta "Pane con [...]" (pane = testa carbo-centrica del piatto); NON diventa "Ricotta con pane" (sposterebbe l'enfasi). Se ORIG dice "Frittata di carciofi", resta "Frittata di [...]" (protein-centric).
3. NON AGGIUNGERE modificatori culinari che non sono nel nome originale o tra gli ingredienti: niente "grattugiato", "saltato", "marinato", "affumicato", "scottato", "tritato", "stufato" inventati. Es. se ingredienti hanno "Parmigiano" e ORIG dice "Pera con parmigiano", il nuovo nome resta "Pera con parmigiano" (NON "Pera con parmigiano grattugiato"). I dettagli di preparazione vanno nello step st, non in nm.
4. nm DEVE riflettere SOLO ingredienti reali in ig. Se ig non ha "riso", MAI "risotto"; se non ha "orzo", MAI "orzotto"; se non ha "pasta", MAI "pasta"/"spaghetti"/"penne".
5. Aggettivi vanno sull'ingrediente corretto. "Pane integrale con ricotta" è OK; "Ricotta integrale" è SBAGLIATO (integrale appartiene al pane, non alla ricotta).
6. Slot SPUNTINO deve avere nome da SNACK (no "Pasta...", no "Frittata di..."). Es. "Yogurt con frutti rossi", "Mela con burro di mandorle".
7. TEMPO: "rapido" = 1-2 step assemblaggio (no cottura lunga); "medio" = 2-3 step tradizionali 15-30min; "elaborato" = 3 step con tecniche 30+min.
8. Niente apostrofi (scrivi "olio di oliva" non "olio d'oliva"). Niente simboli (usa "gradi" non °, "minuti" non min).
9. Output: SOLO array JSON puro, no markdown, no commenti.

ESEMPI POSITIVI:
- ORIG="Pasta ceci patate cipolla", ig=[pasta, ceci, patate, cipolle] → nm="Pasta e ceci con patate", sb="Primo della tradizione contadina"
- ORIG="Pane integrale con ricotta e miele", ig=[pane integrale, ricotta, miele] → nm="Pane integrale con ricotta e miele" (mantieni l'ordine carbo→protein), sb="Colazione semplice e cremosa"
- ORIG="Pera con parmigiano", ig=[pere, parmigiano] → nm="Pera con parmigiano", sb="Snack dolce-salato classico" (NIENTE "grattugiato")
- ORIG="Yogurt greco con frutti di bosco e granola", ig=[yogurt greco, granola, mirtilli, miele] → nm="Yogurt greco con granola e mirtilli", sb="Colazione cremosa e croccante"

ESEMPI NEGATIVI (NON FARE):
- ORIG="Pane integrale con ricotta e miele" → nm="Ricotta con pane integrale e miele" ❌ ORDINE INVERTITO. La testa è "Pane con [...]".
- ORIG="Pera con parmigiano" → nm="Pera con parmigiano grattugiato" ❌ MODIFICATORE INVENTATO. "Grattugiato" non è in ORIG né in ig.
- nm="Pane integrale ricotta miele" ❌ ELENCO senza preposizioni. Aggiungi "con" o "e".
- nm="Ricotta integrale" ❌ AGGETTIVO SBAGLIATO. La ricotta non è integrale.
- ORIG="Pasta con scamorza e salame" per slot SPUNTINO ❌ NON è snack. Trasforma in "Tartina al formaggio".`;

  const profile = skeleton.profile_snapshot || {};
  const slotToTempo = (slotT) => {
    if (slotT === 'col' || slotT === 'sna') return profile.tempo_col_sna || 'rapido';
    if (slotT === 'pra') return profile.tempo_pra || 'medio';
    if (slotT === 'cen') return profile.tempo_cen || 'medio';
    return 'medio';
  };

  const itemsFlat = [];
  for (let d = 0; d < skeleton.days.length; d++) {
    for (let m = 0; m < skeleton.days[d].m.length; m++) {
      for (let i = 0; i < skeleton.days[d].m[m].items.length; i++) {
        const it = skeleton.days[d].m[m].items[i];
        const slotLabel = skeleton.days[d].m[m].ti;
        const slotType = skeleton.days[d].m[m].t;
        const tempo = slotToTempo(slotType);
        const ingr = (it.ig || []).map(x => `${x.q}${x.u || 'g'} ${x.n}`).join(' + ');
        const origName = it._recipe_name || '(nessuno)';
        itemsFlat.push({
          tempo, d, m, i,
          line: `${skeleton.days[d].n} ${slotLabel} [TEMPO=${tempo}] [ORIG="${origName}"]: ${ingr}`
        });
      }
    }
  }

  async function callPart(items) {
    const partText = items.map((x, idx) => `${idx + 1}. ${x.line}`).join('\n');
    const partPrompt = 'Pasti da rinominare e descrivere (ingredienti fissi, ORIG = nome ricetta originale come hint):\n' +
      partText + '\n\n' +
      `Output: array JSON con ESATTAMENTE ${items.length} oggetti, stesso ordine numerato. Schema:\n` +
      '[{"nm":"Nome piatto elegante","sb":"Tecnica o sapore","st":["azione 1","azione 2","azione 3"]}, ...]';
    const r = await api([{ role: 'user', content: partPrompt }], SYS, 4000, HAIKU);
    const cleaned = cj(r);
    const safe = cleaned.replace(/\\(?!["\\/bfnrtu])/g, '');
    return JSON.parse(fixJson(safe));
  }

  let parsed;
  if (itemsFlat.length > 25) {
    const half = Math.ceil(itemsFlat.length / 2);
    const [part1, part2] = await Promise.all([
      callPart(itemsFlat.slice(0, half)),
      callPart(itemsFlat.slice(half))
    ]);
    parsed = [...(Array.isArray(part1) ? part1 : []), ...(Array.isArray(part2) ? part2 : [])];
  } else {
    parsed = await callPart(itemsFlat);
  }

  if (!Array.isArray(parsed)) {
    throw new Error('haikuFillDetails: response non è array piatto (got ' + typeof parsed + ')');
  }

  for (let idx = 0; idx < itemsFlat.length && idx < parsed.length; idx++) {
    const meta = itemsFlat[idx];
    const resp = parsed[idx] || {};
    const item = skeleton.days[meta.d].m[meta.m].items[meta.i];
    item.nm = resp.nm || item.nm || 'Piatto';
    item.sb = resp.sb || item.sb || '';
    item.st = Array.isArray(resp.st) ? resp.st : (item.st || []);
    // Phase 1.5: cleanup _recipe_name dopo merge (UI/persistenza non lo conosce)
    delete item._recipe_name;
  }

  return skeleton;
}

// Tag giorni settimana derivati da giorni_allenamento (Lun=0 ... Dom=6).
// Distribuzione con riposo tra sessioni intense. Dom = meal-prep/riposo.
// Funzione pura: cambia giorni_allenamento → nuovi tag al render, niente
// rigenerazione dei piani necessaria (salvo per i pasti dei giorni che
// cambiano intenso↔riposo, che l'utente può chiedere di rigenerare).
export function mealDayTags(gg) {
  const T = '💪 Allenamento', R = '🛋️ Riposo', P = '🛒 Prep + riposo';
  const patterns = {
    1: [R,R,T,R,R,R,P], 2: [T,R,R,T,R,R,P], 3: [T,R,T,R,T,R,P],
    4: [T,T,R,T,R,T,P], 5: [T,T,R,T,T,T,P], 6: [T,T,T,T,T,T,P],
    7: [T,T,T,T,T,T,T]
  };
  return patterns[parseInt(gg)] || patterns[3];
}

// ─── GATES PRE-GENALL ──────────────────────────────────────────────────────

// Decide se la nota_alimentari/intolleranze richiede una domanda di
// chiarimento ALL'UTENTE prima di generare. Ritorna {question, hint} da
// chiedere in chat prima di generare. Null se la preferenza è già esplicita
// o del tutto assente (nessuna nota → onnivoro default). L'AI, alla risposta
// utente, deve emettere [UPDATE_PROFILO] con note_alimentari ESPLICITO così
// dietClarificationNeeded non scatta più.
export function dietClarificationNeeded(p) {
  if (!p) return null;
  // Idempotenza: se la domanda è già stata posta in passato (flag persistita
  // su onboarding_flags) non la rilanciamo dopo [RIGENERA], anche se la nota
  // aggiornata da Sonnet non matcha i regex sotto. Vedi bug "doppia domanda".
  if (p.onboarding_flags?.dieta_chiesta) return null;
  const note = (p.note_alimentari || '').toLowerCase();
  const intolList = (p.intolleranze || []).map(x => String(x).toLowerCase());
  // Già esplicito → non chiedere
  if (/\b(onnivoro|mangio di tutto|includo\s+carne|con\s+carne)\b/.test(note)) return null;
  if (/vegetariano.*(uova|latticini)|uova.*vegetariano|uova.*latticin/i.test(note)) return null;
  if (/\b(solo vegetali|100\s*%\s*vegetale|proteine\s+solo\s+vegetali|vegano\s+stretto|vegetariano\s+stretto)\b/.test(note)) return null;
  const hasVegan = /vegan/.test(note);
  const hasVeget = /vegetarian/.test(note);
  const mentionsMeat = /\b(carne|manzo|pollo|tacchino|maiale|agnello)\b/.test(note);
  const mentionsFish = /\b(pesc|salmone|tonno|merluzzo)\b/.test(note);
  const excludesFish = /(no\s+pesc|niente\s+pesc)/.test(note) || intolList.some(x => /pesc/.test(x));
  const excludesMeat = /(no\s+carne|niente\s+carne)/.test(note) || intolList.some(x => /\b(carne|manzo|pollo|tacchino|maiale|agnello)\b/.test(x));
  // Caso: solo vegano senza menzione di carne/veget → chiedi per cortesia
  if (hasVegan && !hasVeget && !mentionsMeat && !mentionsFish) {
    return {
      question: 'Vedo che preferisci un piano vegano. Confermi 100% vegetale, o vuoi includere anche uova e latticini (vegetariano)? Le proteine principali saranno legumi, tofu, tempeh, seitan, hummus.',
      hint: 'Possibili risposte e note_alimentari da emettere: (a) "sì vegano" → "vegano stretto, solo proteine vegetali"; (b) "uova e latticini ok" → "vegetariano (uova e latticini ok)"; (c) "anche carne/pesce" → "onnivoro".'
    };
  }
  // Caso: solo vegetariano (senza dire uova/latticini/vegano/carne) → chiedi dettaglio
  if (hasVeget && !hasVegan && !mentionsMeat && !mentionsFish) {
    return {
      question: 'Piano vegetariano: uova e latticini li includo, o preferisci solo proteine vegetali (legumi, tofu, tempeh)?',
      hint: 'Possibili risposte: (a) "uova e latticini ok" → "vegetariano (uova e latticini ok)"; (b) "solo proteine vegetali" → "vegetariano stretto, solo proteine vegetali".'
    };
  }
  // Caso tipico (Gabriele): esclusioni + "ok veg/vegan" ma nessuna menzione di carne
  if ((excludesFish || excludesMeat) && (hasVeget || hasVegan) && !mentionsMeat) {
    const noPart = excludesFish && excludesMeat ? 'niente pesce né carne' : (excludesFish ? 'niente pesce' : 'niente carne');
    return {
      question: `Ok, ${noPart}. Includo anche ${excludesMeat ? 'legumi e proteine vegetali' : 'carne (bianca e rossa) e legumi'}, oppure preferisci piani solo vegetariani/vegani?`,
      hint: `Possibili risposte: (a) "onnivoro" (se excludesMeat=false) → "onnivoro, ${excludesFish ? 'no pesce, ' : ''}varietà con piatti vegetariani/vegani"; (b) "vegetariano" → "vegetariano${excludesFish ? ', no pesce' : ''}"; (c) "vegano" → "vegano${excludesFish ? ', no pesce' : ''}".`
    };
  }
  return null;
}

// Preferenza varietà vs ripetizione: se non specificata, chiedi sì/no.
// Serve a Haiku per decidere se alternare ricette o usarne poche ripetute.
export function varietyClarificationNeeded(p) {
  if (!p) return null;
  // Idempotenza: se la domanda è già stata posta in passato (flag persistita
  // su onboarding_flags) non la rilanciamo dopo [RIGENERA], anche se la nota
  // aggiornata da Sonnet non matcha i regex sotto.
  if (p.onboarding_flags?.varieta_chiesta) return null;
  const note = (p.note_alimentari || '').toLowerCase();
  // Già specificata esplicitamente
  if (/\b(varia|variet|rotazion|alterna|ripete|stesse\s+ricette|piatti\s+fissi|poco\s+vari|pochi\s+piatti)\b/.test(note)) return null;
  return {
    question: 'Ultima cosa: preferisci variare i gusti e alternare ricette diverse ogni giorno, oppure ti trovi bene con pochi piatti fissi che si ripetono? (basta un "sì" per varietà o "no" per pochi piatti fissi)',
    hint: 'Possibili risposte e note_alimentari da AGGIUNGERE alla nota esistente (mantieni il contenuto precedente, appendi): (a) "sì/varietà" → aggiungi " — preferisce varietà"; (b) "no/pochi piatti" → aggiungi " — preferisce pochi piatti fissi ripetuti". NON rimuovere info già presenti.'
  };
}

// Tempo di cucina disponibile (rapido/medio/elaborato) per i 3 raggruppamenti
// slot. Se mancano TUTTI e 3 i campi nel profilo, chiedi una sola domanda
// (con 3 sotto-risposte). Default per profili pre-esistenti: rapido/medio/medio
// applicati dal Haiku writer come fallback (vedi slotToTempo in haikuFillDetails).
export function cookingTimeClarificationNeeded(p) {
  if (!p) return null;
  if (p.onboarding_flags?.tempo_cucina_chiesto) return null;
  // Se almeno UN tempo è settato esplicitamente nel profilo, non chiedere
  // (è già stato configurato via form o chat precedente)
  if (p.tempo_col_sna || p.tempo_pra || p.tempo_cen) return null;
  return {
    question: 'Un\'ultima cosa per i piani alimentari: quanto tempo hai per cucinare? Dimmi per (a) colazione e spuntini, (b) pranzo, (c) cena. Opzioni per ciascuno: **rapido** (assemblato, <15 min), **medio** (15-30 min, tradizionale), **elaborato** (30+ min, articolato). Puoi rispondere in poche parole, es. "rapido per tutto" o "veloce colazione/pranzo, elaborato cena".',
    hint: 'Possibili risposte e mapping per [UPDATE_PROFILO]: (a) "rapido tutto" / "poco tempo" → {"tempo_col_sna":"rapido","tempo_pra":"rapido","tempo_cen":"rapido"}; (b) "veloce colazione e pranzo, elaborato cena" → {"tempo_col_sna":"rapido","tempo_pra":"rapido","tempo_cen":"elaborato"}; (c) "tutto medio" → {"tempo_col_sna":"medio","tempo_pra":"medio","tempo_cen":"medio"}; (d) "amo cucinare" / "tutto elaborato" → {"tempo_col_sna":"medio","tempo_pra":"elaborato","tempo_cen":"elaborato"}. SEMPRE 3 campi insieme con valori coerenti. Default safe: rapido/medio/medio.'
  };
}

// Campi fondamentali richiesti per generare qualsiasi piano.
// Ritorna lista etichette umane dei campi mancanti/invalidi (vuota = profilo OK).
export function profileGaps(p) {
  if (!p) return ['profilo non caricato'];
  const g = [];
  if (!p.nome) g.push('nome');
  if (!p.eta || p.eta < 10 || p.eta > 100) g.push('età');
  if (!['M','F'].includes(p.sesso)) g.push('sesso (M/F)');
  if (!p.peso || p.peso < 30 || p.peso > 250) g.push('peso in kg');
  if (!p.altezza || p.altezza < 100 || p.altezza > 230) g.push('altezza in cm');
  if (!p.obiettivo) g.push('obiettivo (dimagrire / mantenere / massa)');
  if (!p.giorni_allenamento || p.giorni_allenamento < 1 || p.giorni_allenamento > 7) g.push('giorni di allenamento a settimana');
  if (!p.minuti_sessione || p.minuti_sessione < 10) g.push('minuti per sessione');
  if (!['principiante','intermedio','avanzato'].includes(p.livello)) g.push('livello (principiante / intermedio / avanzato)');
  if (!p.pasti_al_giorno || p.pasti_al_giorno < 1) g.push('numero pasti al giorno');
  if (![1,2,4].includes(parseInt(p.durata_dieta))) g.push('durata piano alimentare (1, 2 o 4 settimane)');
  if (![4,6,8,12].includes(parseInt(p.durata_workout))) g.push('durata piano allenamento (4, 6, 8 o 12 settimane)');
  if (!['standalone','integrato','entrambi'].includes(p.stretching_preference)) g.push('preferenza stretching (dedicato / integrato / entrambi)');
  return g;
}

// ─── GENERATOR ENTRY POINTS ────────────────────────────────────────────────

export async function genAll(profile, notes, tipo = 'tutti') {
  // Gate: se mancano campi fondamentali, NON generare. Apri chat, chiedi i
  // dati mancanti e lascia che il flusso [UPDATE_PROFILO]+[RIGENERA] riparta.
  const gaps = profileGaps(profile);
  if (gaps.length > 0) {
    if (typeof openChat === 'function') openChat();
    const list = gaps.map(x => '• ' + x).join('\n');
    addMsg('ai', `Prima di generare i piani mi mancano queste informazioni:\n\n${list}\n\nMe le dici tutte insieme e proseguo con la generazione.`);
    window.S.conv.push({ role: 'assistant', content:
      `Il profilo è incompleto. Campi mancanti o invalidi: ${gaps.join(', ')}. ` +
      `REGOLA CRITICA: quando chiedi all'utente un campo enum-like, ELENCA SEMPRE le opzioni valide SUBITO. Non attendere che l'utente chieda "ho delle opzioni". Valori validi:\n` +
      `- durata_dieta: 1 / 2 / 4 (settimane)\n` +
      `- durata_workout: 4 / 6 / 8 / 12 (settimane per fase)\n` +
      `- stretching_preference: standalone (solo sessioni dedicate) / integrato (nel workout) / entrambi\n` +
      `- livello: principiante / intermedio / avanzato\n` +
      `- sesso: M / F\n` +
      `- obiettivo: dimagrire / mantenere / massa / ricomposizione\n` +
      `Componi UN UNICO messaggio compatto che elenca tutti i campi con le relative opzioni tra parentesi. ` +
      `SALVATAGGIO INCREMENTALE: ad OGNI risposta dell'utente — anche se fornisce UN SOLO campo o pochi campi parziali — emetti SUBITO [UPDATE_PROFILO] con tutti i valori forniti finora. Il client salva ad ogni update, così nessuna informazione si perde se l'utente abbandona. ` +
      `Emetti [RIGENERA] con tipo="${tipo}" SOLO quando il profilo è completo (tutti i campi mancanti elencati sopra sono stati forniti dall'utente). ` +
      `Se mancano ancora campi dopo [UPDATE_PROFILO], NON emettere [RIGENERA] e chiedi gentilmente quelli rimanenti, sempre elencando le opzioni valide.`
    });
    // Ripristina stato tile a 'empty' (no spinner bloccato)
    window.PS.meal = window.PS.meal === 'gen' ? 'empty' : window.PS.meal;
    window.PS.work = window.PS.work === 'gen' ? 'empty' : window.PS.work;
    window.PS.str = window.PS.str === 'gen' ? 'empty' : window.PS.str;
    window.PS.spesa = window.PS.spesa === 'gen' ? 'empty' : window.PS.spesa;
    updateDL();
    return;
  }

  // Gate preferenze alimentari: se la nota utente è ambigua (solo esclusioni
  // o solo "vegetariano/vegano" senza dettaglio) e stiamo per generare il
  // meal, chiedi una domanda gentile prima di coinvolgere Haiku. Evita piani
  // "senza carne" involontari per utenti onnivori che hanno scritto solo
  // "ok vegetariano/vegano" (caso reale visto su Gabriele 2026-04-24).
  if (tipo === 'tutti' || tipo === 'alimentazione') {
    const dietQ = dietClarificationNeeded(profile);
    if (dietQ) {
      if (typeof openChat === 'function') openChat();
      addMsg('ai', dietQ.question);
      // Marca la domanda come posta nel profilo per impedire ri-domande dopo [RIGENERA]
      window.S.profile.onboarding_flags = { ...(window.S.profile.onboarding_flags || {}), dieta_chiesta: true };
      window.saveProfile(window.S.profile).catch(e => console.error('save dieta_chiesta flag', e));
      window.S.conv.push({ role: 'assistant', content:
        `La preferenza alimentare dell'utente è ambigua e ho chiesto: "${dietQ.question}". ` +
        `${dietQ.hint} ` +
        `Quando l'utente risponde, emetti [UPDATE_PROFILO] con il campo note_alimentari aggiornato ` +
        `in modo ESPLICITO (scegli la formulazione più adatta tra quelle suggerite), ` +
        `poi [RIGENERA] tipo="${tipo}" per far ripartire la generazione con le preferenze chiarite.`
      });
      window.PS.meal = window.PS.meal === 'gen' ? 'empty' : window.PS.meal;
      window.PS.work = window.PS.work === 'gen' ? 'empty' : window.PS.work;
      window.PS.str = window.PS.str === 'gen' ? 'empty' : window.PS.str;
      window.PS.spesa = window.PS.spesa === 'gen' ? 'empty' : window.PS.spesa;
      updateDL();
      return;
    }
    // Gate preferenza varietà: se non specificata, chiedi sì/no
    const varQ = varietyClarificationNeeded(profile);
    if (varQ) {
      if (typeof openChat === 'function') openChat();
      addMsg('ai', varQ.question);
      // Marca la domanda come posta nel profilo per impedire ri-domande dopo [RIGENERA]
      window.S.profile.onboarding_flags = { ...(window.S.profile.onboarding_flags || {}), varieta_chiesta: true };
      window.saveProfile(window.S.profile).catch(e => console.error('save varieta_chiesta flag', e));
      window.S.conv.push({ role: 'assistant', content:
        `Ho chiesto all'utente: "${varQ.question}". ${varQ.hint} ` +
        `Quando l'utente risponde, emetti [UPDATE_PROFILO] con note_alimentari arricchita ` +
        `secondo le indicazioni, poi [RIGENERA] tipo="${tipo}".`
      });
      window.PS.meal = window.PS.meal === 'gen' ? 'empty' : window.PS.meal;
      window.PS.work = window.PS.work === 'gen' ? 'empty' : window.PS.work;
      window.PS.str = window.PS.str === 'gen' ? 'empty' : window.PS.str;
      window.PS.spesa = window.PS.spesa === 'gen' ? 'empty' : window.PS.spesa;
      updateDL();
      return;
    }
    // Gate tempo cucina (iter 4 ADR-0006): se mancano i 3 campi tempo_*, chiedi
    // in chat per evitare ricette troppo lunghe per chi ha poco tempo.
    const cookQ = cookingTimeClarificationNeeded(profile);
    if (cookQ) {
      if (typeof openChat === 'function') openChat();
      addMsg('ai', cookQ.question);
      window.S.profile.onboarding_flags = { ...(window.S.profile.onboarding_flags || {}), tempo_cucina_chiesto: true };
      window.saveProfile(window.S.profile).catch(e => console.error('save tempo_cucina_chiesto flag', e));
      window.S.conv.push({ role: 'assistant', content:
        `Ho chiesto all'utente i tempi di cucina disponibili: "${cookQ.question}". ${cookQ.hint} ` +
        `Quando l'utente risponde, emetti [UPDATE_PROFILO] con i 3 campi tempo_col_sna/tempo_pra/tempo_cen, ` +
        `poi [RIGENERA] tipo="${tipo}".`
      });
      window.PS.meal = window.PS.meal === 'gen' ? 'empty' : window.PS.meal;
      window.PS.work = window.PS.work === 'gen' ? 'empty' : window.PS.work;
      window.PS.str = window.PS.str === 'gen' ? 'empty' : window.PS.str;
      window.PS.spesa = window.PS.spesa === 'gen' ? 'empty' : window.PS.spesa;
      updateDL();
      return;
    }
  }
  // Profilo completo: se la chat è aperta, annuncia e chiudila per far vedere
  // il progresso sulle tile in home. Se è già chiusa (es. save da form), nulla.
  const chatPanel = document.getElementById('chatPanel');
  if (chatPanel && chatPanel.classList.contains('open')) {
    addSys('🏠 Generazione avviata — ti riporto alla home per vedere i piani aggiornarsi.', 'info');
    // 1600ms (era 600ms): l'utente segnalava transizione troppo veloce per
    // leggere il messaggio prima della chiusura.
    setTimeout(() => { if (typeof closeChat === 'function') closeChat(); }, 1600);
  }
  const stretchPref = profile.stretching_preference || 'standalone';
  const skipStr = stretchPref === 'integrato';
  const doM = tipo === 'tutti' || tipo === 'alimentazione';
  const doW = tipo === 'tutti' || tipo === 'allenamento';
  const doS = (tipo === 'tutti' || tipo === 'stretching') && !skipStr;
  const doP = tipo === 'tutti' || tipo === 'spesa' || tipo === 'alimentazione';
  // Marca le tile coinvolte come 'In coda…' per evitare confusione con
  // lo stato 'Pronto' del piano precedente: ogni gen* sovrascrive subito
  // la propria tile a 'gen' quando parte. Per single-plan il flash è
  // invisibile (sovrascritto in <100ms), per 'tutti' chiarisce la sequenza.
  if (doM) setTile('meal', 'queue', 'In coda…');
  if (doW) setTile('work', 'queue', 'In coda…');
  if (doS) setTile('str', 'queue', 'In coda…');
  if (doP) setTile('spesa', 'queue', 'In coda…');
  if (doM) await genMeal(profile, notes);
  if (doW) await genWork(profile, notes);
  if (doS) await genStr(profile, notes);
  if (doP) {
    // La spesa dipende semanticamente dal piano alimentare: se abbiamo appena
    // provato a generare meal e non è riuscito, non ha senso generare una
    // spesa scorrelata. Marchiamo la tile in errore con messaggio chiaro.
    if (doM && window.PS.meal === 'err') {
      window.PS.spesa = 'err';
      setTile('spesa', 'err', 'Il piano alimentare non è riuscito. Rigenera prima l\'alimentazione.');
    } else {
      await genSpesa(profile, notes);
    }
  }
  updateDL();
  const nome = profile.nome || '';
  const errs = [doM&&window.PS.meal==='err'?'alimentazione':null, doW&&window.PS.work==='err'?'allenamento':null, doS&&window.PS.str==='err'?'stretching':null, doP&&window.PS.spesa==='err'?'spesa':null].filter(Boolean);
  const oks = (doM&&window.PS.meal==='ok'?1:0)+(doW&&window.PS.work==='ok'?1:0)+(doS&&window.PS.str==='ok'?1:0)+(doP&&window.PS.spesa==='ok'?1:0);
  if (!errs.length) {
    addMsg('ai', `Tutti i piani sono pronti${nome ? ', **' + nome + '**' : ''} 🎉\n\nLi trovi in home — sono salvati sul tuo account e accessibili da qualsiasi dispositivo.`);
  } else if (oks > 0) {
    const verbo = errs.length > 1 ? 'non sono riusciti' : 'non è riuscito';
    addMsg('ai', `${oks} ${oks === 1 ? 'piano generato' : 'piani generati'}. ${errs.map(x=>'**'+x+'**').join(' e ')} ${verbo}.\n\nPuoi riprovare dalla home: tocca **Riprova** sulla card del piano.`);
  } else {
    addMsg('ai', 'Nessun piano è stato generato. Riprova dalla home tra un momento.');
  }
}

// genMeal orchestratore: composeMealSkeleton (JS) → ciqualEnrich (totals) → haikuFillDetails (LLM writer).
// Vedi ADR-0006 + brief preferenze alimentari per architettura completa.
export async function genMeal(profile, notes) {
  // Phase 1.6: gate clarification tempo cottura anche su genMeal standalone
  // (non solo genAll). Se profile non ha mai impostato tempo_*, chiedi in chat
  // prima di generare — il composer filtra ricette per tempo_minuti matching.
  const cookQ = cookingTimeClarificationNeeded(profile);
  if (cookQ) {
    if (typeof openChat === 'function') openChat();
    if (typeof addMsg === 'function') addMsg('ai', cookQ.question);
    if (window.S && window.S.profile) {
      window.S.profile.onboarding_flags = { ...(window.S.profile.onboarding_flags || {}), tempo_cucina_chiesto: true };
      try { await window.saveProfile(window.S.profile); } catch (e) { console.error('save tempo_cucina_chiesto flag', e); }
      window.S.conv.push({ role: 'assistant', content:
        `Ho chiesto all'utente i tempi di cucina disponibili: "${cookQ.question}". ${cookQ.hint} ` +
        `Quando l'utente risponde, emetti [UPDATE_PROFILO] con i 3 campi tempo_col_sna/tempo_pra/tempo_cen, ` +
        `poi [RIGENERA] tipo="meal".`
      });
    }
    window.PS.meal = window.PS.meal === 'gen' ? 'empty' : window.PS.meal;
    if (typeof updateDL === 'function') updateDL();
    return;
  }

  window.PS.meal = 'gen';
  setTile('meal', 'gen', 'Compongo lo scheletro del piano…', 10);

  try {
    const skeleton = await composeMealSkeleton(profile, window.sb);
    setTile('meal', 'gen', 'Calcolo macro reali…', 40);

    const enriched = await ciqualEnrich(skeleton);
    setTile('meal', 'gen', 'Scrivo le ricette…', 70);

    const final = await haikuFillDetails(enriched);
    setTile('meal', 'gen', 'Salvo il piano…', 90);

    window.S.plans.meal = final;
    const planId = await savePlan('meal', final);
    window.S.plans.meal._id = planId;
    window.S.plans.meal._createdAt = new Date().toISOString();

    try {
      await persistMealPlanStructured(planId, final.days);
    } catch (structErr) {
      console.error('genMeal persist structured (non-blocking)', structErr);
    }

    window.PS.meal = 'ok';
    setTile('meal', 'ok', 'Pronto');
  } catch (e) {
    console.error('genMeal', e);
    logGenError('meal_compose', e, { profile_intolleranze: profile.intolleranze, error_message: e.message });
    // UX message specifico per pool insufficient
    const friendlyMsg = /POOL_INSUFFICIENT|POOL_PROTEIN_INSUFFICIENT|POOL_CARB_INSUFFICIENT|POOL_VERDURA_INSUFFICIENT/.test(e.message)
      ? 'Profilo troppo restrittivo: pochi alimenti compatibili. Rilassa 1 intolleranza e riprova.'
      : 'Piano alimentare non riuscito. Riprova quando vuoi.';
    setTile('meal', 'err', friendlyMsg);
    window.PS.meal = 'err';
  }
}

// ─── SHOPPING LIST HELPERS ─────────────────────────────────────────────────
// Helper consumati da genSpesa per render della lista. PR-3c originale li
// aveva inline in coach/index.html dopo persistMealPlanStructured; PR-7 ha
// erroneamente perso il blocco (sub-agent grep falso negativo perché il
// blocco era stato già spostato fuori da inline al PR-3b cleanup).
// Restored in genSpesa hot-fix.

// Arrotonda una quantità alla pack_size commerciale (superiore).
// Es. 237g yogurt con pack_size=125 → 250 (2 confezioni).
function roundToPack(qty, packSize) {
  if (!packSize || packSize <= 0) {
    // Fallback: arrotonda al 10g superiore per leggibilità
    return Math.ceil(qty / 10) * 10;
  }
  return Math.ceil(qty / packSize) * packSize;
}

// Formatta quantità per display in lista spesa.
// Converte g→kg se ≥ 1000, ml→L se ≥ 1000.
function formatQty(qty, unit) {
  const q = Number(qty);
  if (unit === 'g' && q >= 1000) {
    const kg = q / 1000;
    return (kg % 1 === 0 ? kg.toFixed(0) : kg.toFixed(1)) + 'kg';
  }
  if (unit === 'ml' && q >= 1000) {
    const l = q / 1000;
    return (l % 1 === 0 ? l.toFixed(0) : l.toFixed(1)) + 'L';
  }
  if (unit === 'pz') return q + (q === 1 ? ' pz' : ' pz');
  return Math.round(q) + unit;
}

// Mappa categoria DB → nome UI con emoji (lista spesa).
const CATEGORY_LABEL = {
  proteine:        '🥩 Proteine',
  carboidrati:     '🍝 Carboidrati',
  verdura_frutta:  '🥬 Frutta e verdura',
  latticini:       '🥛 Latticini',
  dispensa:        '🏪 Dispensa'
};
const CATEGORY_ORDER = ['proteine','carboidrati','verdura_frutta','latticini','dispensa'];

export async function genSpesa(profile, notes) {
  window.PS.spesa = 'gen'; setTile('spesa', 'gen', 'Compongo la lista della spesa…', 20);
  const planId = window.S.plans.meal && window.S.plans.meal._id;
  if (!planId) {
    console.warn('genSpesa: meal plan senza planId (piano legacy o non ancora generato)');
    setTile('spesa', 'err', 'Rigenera prima il piano alimentare: la spesa viene calcolata dai suoi ingredienti.');
    window.PS.spesa = 'err';
    return;
  }
  setTile('spesa', 'gen', 'Aggrego ingredienti dal piano…', 50);
  try {
    // La shopping_list_view aggrega automaticamente via SQL (SUM quantity_base
    // group by canonical_name,unit). Zero chiamate AI, aggregazione deterministica.
    const { data: rows, error } = await window.sb
      .from('shopping_list_view')
      .select('category, name, total_quantity, unit, pack_size, is_unknown')
      .eq('plan_id', planId);
    if (error) throw error;
    if (!rows || rows.length === 0) {
      throw new Error('La view non ha restituito ingredienti: forse il piano è stato salvato senza struttura');
    }
    // Raggruppa per categoria mantenendo l'ordine deterministico
    const byCategory = {};
    rows.forEach(r => {
      const cat = r.category;
      if (!byCategory[cat]) byCategory[cat] = [];
      const qtyRounded = roundToPack(r.total_quantity, r.pack_size);
      const qtyFmt = formatQty(qtyRounded, r.unit);
      const nameDisplay = r.name.charAt(0).toUpperCase() + r.name.slice(1);
      byCategory[cat].push((r.is_unknown ? '⚠️ ' : '') + nameDisplay + ' ' + qtyFmt);
    });
    const lista = CATEGORY_ORDER
      .filter(cat => byCategory[cat] && byCategory[cat].length > 0)
      .map(cat => ({ n: CATEGORY_LABEL[cat], items: byCategory[cat].sort() }));
    // Meal prep è ora una lista statica di suggerimenti basati sulle quantità
    // principali. Niente AI: semplice regola.
    const topProtein = rows
      .filter(r => r.category === 'proteine')
      .sort((a,b) => Number(b.total_quantity) - Number(a.total_quantity))[0];
    const topCarb = rows
      .filter(r => r.category === 'carboidrati')
      .sort((a,b) => Number(b.total_quantity) - Number(a.total_quantity))[0];
    const prepItems = ['Lava e taglia verdure a porzioni (porta a 3 contenitori)'];
    if (topProtein) {
      prepItems.push('Grilla/cuoci ' + formatQty(roundToPack(topProtein.total_quantity, topProtein.pack_size), topProtein.unit) +
                     ' di ' + topProtein.name + ' per le porzioni dei prossimi 3 giorni');
    }
    if (topCarb) {
      prepItems.push('Cuoci ' + formatQty(roundToPack(topCarb.total_quantity * 0.6, topCarb.pack_size), topCarb.unit) +
                     ' di ' + topCarb.name + ' per i primi 3 giorni');
    }
    prepItems.push('Porziona snack e frutta in contenitori singoli');
    prepItems.push('Prepara 1L di acqua con limone per la settimana');
    const prep = { t: '🛒 Meal Prep della Domenica', items: prepItems };
    window.S.plans.spesa = { prep, lista };
    window.S.plans.spesa._createdAt = new Date().toISOString();
    await savePlan('spesa', window.S.plans.spesa);
    window.PS.spesa = 'ok'; setTile('spesa', 'ok', 'Pronto');
  } catch(e) {
    console.error('genSpesa', e);
    logGenError('spesa', e, {});
    setTile('spesa', 'err', 'Lista della spesa non riuscita. Riprova quando vuoi.');
    window.PS.spesa = 'err';
  }
}

// BKL-091: SECONDO PASSAGGIO di arricchimento (best-effort, mai bloccante).
// Per ogni giorno di ogni fase, genera descrizioni strutturate e le salva come
// 4° elemento della tupla esercizio: ex[i][3] = {setup, steps[], errori[], resp, sicurezza}.
// Batch per giorno per non troncare. Ogni fallimento (rete/shape/parse) lascia
// l'esercizio col solo hint ex[i][2] → nessuna regressione sul piano base.
async function enrichWorkout(P, profile, SYS) {
  for (let pi = 0; pi < P.length; pi++) {
    const days = Array.isArray(P[pi].days) ? P[pi].days : [];
    for (let di = 0; di < days.length; di++) {
      const flat = [];
      (days[di].sections || []).forEach(s => {
        (s.ex || []).forEach(ex => { if (Array.isArray(ex)) flat.push(ex); });
      });
      if (!flat.length) continue;
      try {
        const prompt = workEnrich(flat.map(e => ({ n: e[0], d: e[1] })), profile);
        const r = await api([{ role: 'user', content: prompt }], SYS, 4000, HAIKU);
        const rich = JSON.parse(fixJson(cj(r)));
        if (Array.isArray(rich) && rich.length === flat.length) {
          for (let i = 0; i < flat.length; i++) {
            const o = rich[i];
            if (o && typeof o === 'object' && !Array.isArray(o)) flat[i][3] = o;
          }
        } else {
          console.warn('enrichWorkout shape mismatch', { pi, di, got: rich && rich.length, exp: flat.length });
        }
      } catch (e) {
        console.warn('enrichWorkout failed', { pi, di }, e);
      }
    }
  }
}

// BKL-092: SECONDO PASSAGGIO di arricchimento per lo STRETCHING (gemello di enrichWorkout).
// DATA = array di 7 giorni; ogni giorno ha sections[].ex[] (o ex[] flat). Batch per giorno,
// best-effort: ogni fallimento lascia lo stretch col solo hint ex[i][2] → piano intatto.
async function enrichStretch(DATA, profile, SYS) {
  const days = Array.isArray(DATA) ? DATA : [];
  for (let di = 0; di < days.length; di++) {
    const d = days[di];
    const secs = (d && d.sections && d.sections.length) ? d.sections : [{ ex: (d && d.ex) || [] }];
    const flat = [];
    secs.forEach(s => { (s.ex || []).forEach(ex => { if (Array.isArray(ex)) flat.push(ex); }); });
    if (!flat.length) continue;
    try {
      const prompt = strEnrich(flat.map(e => ({ n: e[0], d: e[1] })), profile);
      const r = await api([{ role: 'user', content: prompt }], SYS, 4000, HAIKU);
      const rich = JSON.parse(fixJson(cj(r)));
      if (Array.isArray(rich) && rich.length === flat.length) {
        for (let i = 0; i < flat.length; i++) {
          const o = rich[i];
          if (o && typeof o === 'object' && !Array.isArray(o)) flat[i][3] = o;
        }
      } else {
        console.warn('enrichStretch shape mismatch', { di, got: rich && rich.length, exp: flat.length });
      }
    } catch (e) {
      console.warn('enrichStretch failed', { di }, e);
    }
  }
}

export async function genWork(profile, notes) {
  const SYS_WORK = 'Personal trainer esperto. Rispondi SOLO JSON valido, zero testo aggiuntivo.';
  window.PS.work = 'gen'; setTile('work', 'gen', 'Preparo la Fase 1…', 10);
  let P = [];
  const expectedDays = profile.giorni_allenamento || 3;
  // Deep validation: la fase è valida solo se ogni giorno ha almeno una
  // sezione con almeno un esercizio. Haiku a volte restituisce days con
  // sections:[] — passa il check shallow ma il piano è inutilizzabile.
  function validPhase(parsed, expectedDays) {
    if (!parsed || !Array.isArray(parsed.days) || parsed.days.length === 0) return false;
    if (parsed.days.length < expectedDays) return false;
    return parsed.days.every(d => Array.isArray(d.sections) && d.sections.length > 0 &&
      d.sections.some(s => Array.isArray(s.ex) && s.ex.length > 0));
  }
  // Fase 1 con retry su struttura invalida o days/sections vuoti
  let p1Ok = false, p1Err = null;
  for (let attempt = 1; attempt <= 3 && !p1Ok; attempt++) {
    try {
      setTile('work', 'gen', attempt === 1 ? 'Preparo la Fase 1…' : 'Ritento Fase 1 (tentativo ' + attempt + ' di 3)…', 10 + attempt*8);
      const r1 = await api([{ role: 'user', content: workP1(profile, notes, window.S.lastMeasurement) }], SYS_WORK, 5000, HAIKU);
      const parsed = JSON.parse(fixJson(cj(r1)));
      if (!validPhase(parsed, expectedDays)) {
        console.warn('genWork P1 attempt', attempt, 'invalid phase', parsed);
        p1Err = new Error('shape non valida');
        if (attempt < 3) logGenError('work_p1_retry', p1Err, { attempt, reason: 'shape_invalid' });
        continue;
      }
      P.push(parsed); p1Ok = true;
    } catch(e) {
      console.error('genWork P1 attempt', attempt, e);
      p1Err = e;
      if (attempt < 3) logGenError('work_p1_retry', e, { attempt });
    }
  }
  if (!p1Ok) {
    logGenError('work_p1', p1Err, { attempt_count: 3, expected_days: expectedDays });
    setTile('work', 'err', 'Piano allenamento non riuscito. Riprova quando vuoi.');
    window.PS.work = 'err'; return;
  }
  // Fase 2 con retry su struttura invalida o days/sections vuoti
  let p2Ok = false, p2Err = null;
  for (let attempt = 1; attempt <= 3 && !p2Ok; attempt++) {
    try {
      setTile('work', 'gen', attempt === 1 ? 'Preparo la Fase 2…' : 'Ritento Fase 2 (tentativo ' + attempt + ' di 3)…', 55 + attempt*8);
      const r2 = await api([{ role: 'user', content: workP2(profile, notes, window.S.lastMeasurement) }], SYS_WORK, 5000, HAIKU);
      const parsed = JSON.parse(fixJson(cj(r2)));
      if (!validPhase(parsed, expectedDays)) {
        console.warn('genWork P2 attempt', attempt, 'invalid phase', parsed);
        p2Err = new Error('shape non valida');
        if (attempt < 3) logGenError('work_p2_retry', p2Err, { attempt, reason: 'shape_invalid' });
        continue;
      }
      P.push(parsed); p2Ok = true;
    } catch(e) {
      console.error('genWork P2 attempt', attempt, e);
      p2Err = e;
      if (attempt < 3) logGenError('work_p2_retry', e, { attempt });
    }
  }
  if (!p2Ok) {
    logGenError('work_p2', p2Err, { attempt_count: 3, expected_days: expectedDays });
    setTile('work', 'err', 'Piano allenamento non riuscito. Riprova quando vuoi.');
    window.PS.work = 'err'; return;
  }
  // BKL-091: arricchimento spiegazioni esercizi (best-effort, non blocca il salvataggio)
  setTile('work', 'gen', 'Arricchisco le spiegazioni degli esercizi…', 92);
  try { await enrichWorkout(P, profile, SYS_WORK); } catch (e) { console.warn('enrichWorkout outer', e); }
  window.S.plans.workout = P;
  window.S.plans.workout._createdAt = new Date().toISOString();
  const wkPlanId = await savePlan('workout', window.S.plans.workout);
  if (wkPlanId) window.S.plans.workout._id = wkPlanId;  // BKL-036: needed for workout_completions persistence
  window.PS.work = 'ok'; setTile('work', 'ok', 'Pronto');
}

export async function genStr(profile, notes) {
  const SYS_STR = "Esperto mobilita e stretching. Rispondi SOLO JSON valido, zero testo aggiuntivo. Evita apostrofi nelle stringhe JSON.";
  window.PS.str = 'gen';
  let strErr = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    setTile('str', 'gen', attempt === 1 ? 'Preparo lo stretching…' : 'Ritento (tentativo ' + attempt + ' di 3)…', 20 + attempt*20);
    try {
      const r = await api([{ role: 'user', content: strP(profile, notes) }], SYS_STR, 4000, HAIKU);
      const cleaned = fixJson(cj(r));
      window.S.plans.stretch = JSON.parse(cleaned);
      // BKL-092: arricchimento spiegazioni stretch (best-effort, non blocca il salvataggio)
      setTile('str', 'gen', 'Arricchisco le spiegazioni degli stretch…', 85);
      try { await enrichStretch(window.S.plans.stretch, profile, 'Esperto mobilità e stretching. Rispondi SOLO con un array JSON valido, zero testo aggiuntivo.'); } catch (e) { console.warn('enrichStretch outer', e); }
      window.S.plans.stretch._createdAt = new Date().toISOString();
      const strPlanId = await savePlan('stretch', window.S.plans.stretch);
      if (strPlanId) window.S.plans.stretch._id = strPlanId;  // BKL-036: needed for stretch_completions persistence
      window.PS.str = 'ok'; setTile('str', 'ok', 'Pronto');
      return;
    } catch(e) {
      console.error('genStr attempt', attempt, e);
      strErr = e;
      if (attempt < 3) logGenError('str_retry', e, { attempt });
      if (attempt === 3) {
        logGenError('str', strErr, { attempt_count: 3 });
        setTile('str', 'err', 'Stretching non riuscito. Riprova quando vuoi.');
        window.PS.str = 'err';
      }
    }
  }
}

// ─── IMPACT DETECTION (BKL-002) ────────────────────────────────────────────
// Dato un profilo precedente e uno nuovo, ritorna il tipo da passare a genAll.
// Usato da submitForm() per rigenerare solo i piani impattati dal cambio.
// Mapping: campi solo-meal → 'alimentazione', campi workout/stretch → 'allenamento',
//          campi globali (peso/obiettivo/macro) → 'tutti', nessun cambio → null.
export function detectImpactedPlans(prev, next) {
  if (!prev || !next) return 'tutti';
  const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const ch = (fields) => fields.some(f => !eq(prev[f], next[f]));
  const mealFields = ['intolleranze', 'note_alimentari', 'durata_dieta', 'tempo_col_sna', 'tempo_pra', 'tempo_cen', 'pasti_al_giorno', 'preferenze'];
  const workoutFields = ['limitazioni', 'minuti_sessione', 'giorni_allenamento', 'durata_workout', 'livello', 'attrezzatura', 'stretching_preference'];
  const allFields = ['peso', 'altezza', 'eta', 'sesso', 'obiettivo', 'kcal_target', 'proteine_target', 'carbo_target', 'grassi_target'];
  if (ch(allFields)) return 'tutti';
  const meal = ch(mealFields);
  const workout = ch(workoutFields);
  if (meal && workout) return 'tutti';
  if (meal) return 'alimentazione';
  if (workout) return 'allenamento';
  return null;
}
